'use client';

import { useUiContent } from '@/components/ui-content-provider';
import { Locale, countryLanguages, defaultLanguage } from 'shared/src/types/languages.type';
import { processContact } from '@/server/send-email';
import React, { useState } from 'react';
import {
  ExploreField,
  ExploreFormError,
  ExploreFormSuccess,
  ExploreSubmit,
  exploreFieldClass,
  exploreTextAreaClass,
} from '@/components/explore/explore-form-fields';

interface ContactFormProps {
  lang: string;
  withCV?: boolean;
}

/**
 * The contact form on the Inception line: hairline-underlined fields on the
 * canvas, no numbered sections, no boxed card. Field labels still come from
 * `ui.contactForm`, which is translated in every locale.
 */
export default function ContactForm({ lang }: ContactFormProps) {
  const ui = useUiContent();
  const locale = countryLanguages[lang as Locale] ?? defaultLanguage;
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    hp: '',
  });

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const result = await processContact({
        name: formData.firstName,
        fname: formData.firstName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        message: formData.message,
        hp: formData.hp,
        locale: locale.path,
      });

      if (result && result.ok === false) {
        setStatus('error');
        return;
      }

      setStatus('success');
      setFormData({ firstName: '', email: '', phone: '', company: '', message: '', hp: '' });
    } catch (error) {
      setStatus('error');
    }
  };

  const t = ui.contactForm;

  if (status === 'success') {
    return <ExploreFormSuccess title={t.successTitle} body={t.successMessage} />;
  }

  return (
    <form className="space-y-8" onSubmit={handleSubmit} data-contact-form>
      {/* Honeypot: hidden from humans, bots tend to fill it. */}
      <input
        type="text"
        name="hp"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={formData.hp}
        onChange={(e) => setFormData((prev) => ({ ...prev, hp: e.target.value }))}
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
      />

      <div className="grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2">
        <ExploreField id="fname" label={t.firstName} required>
          <input
            type="text"
            id="fname"
            name="fname"
            required
            autoComplete="given-name"
            value={formData.firstName}
            onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
            className={exploreFieldClass}
            placeholder={t.firstNamePlaceholder}
          />
        </ExploreField>

        <ExploreField id="email" label={t.emailAddress} required>
          <input
            type="email"
            id="email"
            name="email"
            required
            autoComplete="email"
            value={formData.email}
            onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
            className={exploreFieldClass}
            placeholder={t.emailPlaceholder}
          />
        </ExploreField>

        <ExploreField id="phone" label={t.phoneNumber}>
          <input
            type="tel"
            id="phone"
            name="phone"
            autoComplete="tel"
            value={formData.phone}
            onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
            className={exploreFieldClass}
            placeholder={t.phonePlaceholder}
          />
        </ExploreField>

        <ExploreField id="company" label={t.company}>
          <input
            type="text"
            id="company"
            name="company"
            autoComplete="organization"
            value={formData.company}
            onChange={(e) => setFormData((prev) => ({ ...prev, company: e.target.value }))}
            className={exploreFieldClass}
            placeholder={t.companyPlaceholder}
          />
        </ExploreField>
      </div>

      <ExploreField id="message" label={t.message} required>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
          rows={5}
          required
          className={exploreTextAreaClass}
          placeholder={t.messagePlaceholder}
        />
      </ExploreField>

      <div className="flex flex-wrap items-center gap-4 pt-2">
        <ExploreSubmit
          label={t.sendMessage}
          loadingLabel={t.sending}
          loading={status === 'loading'}
        />
        {status === 'error' && <ExploreFormError message={t.errorMessage} />}
      </div>
    </form>
  );
}
